import type { RouteRecordRaw } from "vue-router";
import { createRouter, createWebHistory } from "vue-router";
import {
	getAuth,
	signInAnonymously,
	signInWithEmailAndPassword,
} from "firebase/auth";

import { useUserStore } from "@/store/useUserStore";

import WoningPage from "@/pages/financiering/sections/WoningPage.vue";
import FinancieringPage from "@/pages/financiering/FinancieringPage.vue";
import FinancieringsOpzetPage from "@/pages/financiering/sections/FinancieringsOpzetPage.vue";
import LeningdelenPage from "@/pages/financiering/sections/LeningdelenPage.vue";
import MaximaleHypotheekPage from "@/pages/financiering/sections/MaximaleHypotheekPage.vue";
import MaandlastenPage from "@/pages/financiering/sections/MaandlastenPage.vue";
import PDFMakenPage from "@/pages/financiering/sections/PDFMakenPageWrap.vue";
import OptimalisatiePage from "@/pages/financiering/sections/OptimalisatiePage.vue";
import NotFoundPage from "@/pages/NotFoundPage.vue";
import NoDossierPage from "@/pages/NoDossierPage.vue";
import DataRequestsPage from "@/pages/DataRequestsPage.vue";

// Imports for Dossier Section
import DossierHomePage from "@/pages/dossiers-page/DossierHomePage.vue";
import DossierPage from "@/pages/dossiers-page/DossierPage.vue";
import NewDossierPage from "@/pages/dossiers-page/NewDossierPage.vue";
import DossierListPage from "@/pages/dossiers-page/DossierListPage.vue";
import SingleDossierPage from "@/pages/SingleDossierPage.vue";
import FileUploadPage from "@/pages/file-upload/FileUploadPage.vue";
import SettingsPage from "@/pages/settings/SettingsPage.vue";

import LoanApplicationOverviewPage from "@/pages/loan-application/LoanApplicationOverviewPage.vue";
import LoanApplicationEditPage from "@/pages/loan-application/LoanApplicationEditPage.vue";
import LoanApplicationDetailsPage from "@/pages/loan-application/LoanApplicationDetailsPage.vue";
import LoanApplicationDocumentsPage from "@/pages/loan-application/LoanApplicationDocumentsPage.vue";
import LoanApplicationMessagesPage from "@/pages/loan-application/LoanApplicationMessagesPage.vue";
import LoanOffersPage from "@/pages/LoanOffersPage.vue";
import AdvicePage from "@/pages/advice/AdvicePage.vue";
import PensioenAdvicePage from "@/pages/advice/sections/PensioenAdvicePage.vue";
import InventarisationPage from "@/pages/inventarisation/InventarisationPage.vue";
import InventarisationIncomePage from "@/pages/inventarisation/InventarisationIncomePage.vue";
import InventarisationCustomerPage from "@/pages/inventarisation/InventarisationCustomerPage.vue";
import InventarisationAssetsPage from "@/pages/inventarisation/InventarisationAssetsPage.vue";
import InventarisationLiabilitiesPage from "@/pages/inventarisation/InventarisationLiabilitiesPage.vue";
import InventarisationCurrentObjectsPage from "@/pages/inventarisation/InventarisationCurrentObjectsPage.vue";

import MeetingPage from "@/pages/MeetingPage.vue";
import MeetingTranscriptionPage from "@/pages/MeetingTranscriptionPage.vue";
import MeetingSummaryPage from "@/pages/MeetingSummaryPage.vue";
import MeetingsOverviewPage from "@/pages/MeetingsOverviewPage.vue";

import AgencyPage from "@/pages/AgencyPage.vue";
import TheDashboard from "@/pages/TheDashboard.vue";
import ExistingMortgagePage from "@/pages/inventarisation/ExistingMortgagePage.vue";
import { useZero } from "@/composables/useZero";
import { queries } from "@loanlink-nl/zero";

export const routes: RouteRecordRaw[] = [
	{
		path: "/",
		name: "Root",
		redirect: "/app",
	},
	{
		path: "/data-request/:dataRequestId/complete",
		name: "DataRequestCompleted",
		meta: { public: true },
		component: () => import("@/pages/DataRequestCompletedPage.vue"),
		beforeEnter: async () => {
			const userStore = useUserStore();
			if (!userStore.user) {
				const auth = getAuth();
				await signInAnonymously(auth);
			}
		},
	},
	{
		path: "/auth/firebase",
		name: "Auth",
		meta: { public: true, requiresLoggedOut: true },
		redirect: (to) => {
			const nextQuery = to.query.oobCode ? { oobCode: to.query.oobCode } : {};

			switch (to.query.mode) {
				case "resetPassword":
					return { name: "ResetPassword", query: nextQuery };
				case "recoverEmail":
					return { name: "RecoverEmail", query: nextQuery };
				case "verifyEmail":
					return { name: "VerifyEmail", query: nextQuery };
				default:
					return { name: "Login" };
			}
		},
	},
	{
		path: "/auth/wachtwoord-wijzigen",
		name: "ResetPassword",
		meta: { public: true, requiresLoggedOut: true },
		component: () => import("@/pages/AuthResetPasswordPage.vue"),
	},
	{
		path: "/auth/recover-email",
		name: "RecoverEmail",
		meta: { public: true, requiresLoggedOut: true },
		component: () => import("@/pages/AuthRecoverEmailPage.vue"),
	},
	{
		path: "/auth/verify-email",
		name: "VerifyEmail",
		meta: { public: true },
		component: () => import("@/pages/AuthVerifyEmailPage.vue"),
	},
	{
		path: "/login",
		name: "Login",
		meta: { public: true, requiresLoggedOut: true, title: "Inloggen" },
		beforeEnter: async () => {
			const dev = import.meta.env.DEV;
			const autoLogin = import.meta.env.VITE_AUTO_LOGIN === "true";

			if (dev && autoLogin) {
				const user = await signInWithEmailAndPassword(
					getAuth(),
					import.meta.env.VITE_EMAIL,
					import.meta.env.VITE_PASSWORD,
				);

				if (user) {
					return { name: "App" };
				}
			}
		},
		component: () => import("@/pages/AuthLoginPage.vue"),
	},
	{
		path: "/aanmelden",
		name: "Register",
		meta: { public: true, requiresLoggedOut: true, title: "Aanmelden" },
		component: () => import("@/pages/RegistrationPage.vue"),
	},
	{
		path: "/financieringsopzet",
		redirect: { name: "Dossier Selecteren" },
	},
	{
		path: "/app",
		name: "App",
		component: TheDashboard,
		redirect: () => {
			const role = useUserStore().role;

			if (role === "admin") {
				return { name: "Admin" };
			}

			return { name: "Dossier Selecteren" };
		},
		children: [
			{
				path: "geen-dossier",
				name: "Geen dossier",
				component: NoDossierPage,
				beforeEnter: async () => {
					const zero = useZero().value;
					const availableDossiers = await zero.run(queries.dossier.all());
					if ((availableDossiers?.length ?? 0) > 0) {
						return { name: "App" };
					}
				},
			},
			{
				path: "admin",
				name: "Admin",
				meta: { requiresAdmin: true },
				redirect: { name: "Interest mails" },
				component: () => import("@/pages/admin/AdminPage.vue"),
				children: [
					{
						path: "interest-mails/:interestEmailId?",
						name: "Interest mails",
						component: () => import("@/pages/admin/InterestMails.vue"),
					},
					{
						path: "interest-sheets/:interestSheetId?",
						name: "Interest sheets",
						component: () => import("@/pages/admin/InterestSheets.vue"),
					},
				],
			},
			{
				path: "dossiers",
				name: "Dossiers",
				meta: { requiresIntermediary: true, title: "Dossiers" },
				component: DossierPage,
				redirect: { name: "Dossier Selecteren" },
				children: [
					{
						path: "",
						name: "Dossier Selecteren",
						component: DossierListPage,
					},
					{
						path: "dossier-selecteren",
						redirect: { name: "Dossier Selecteren" },
					},
					{
						path: "nieuw",
						name: "Nieuw Dossier Maken",
						meta: { title: "Nieuw dossier" },
						component: NewDossierPage,
					},
				],
			},
			{
				path: "gesprekken",
				children: [
					{
						path: "",
						name: "MeetingsOverview",
						component: MeetingsOverviewPage,
					},
					{
						path: ":meetingId",
						name: "Meeting",
						redirect: { name: "Samenvatting" },
						component: MeetingPage,
						children: [
							{
								path: "",
								alias: "samenvatting",
								name: "Samenvatting",
								component: MeetingSummaryPage,
							},
							{
								path: "transcript",
								name: "Transcript",
								component: MeetingTranscriptionPage,
							},
						],
					},
				],
			},
			{
				path: "kantoor",
				children: [
					{
						path: "",
						name: "AgencyPage",
						component: AgencyPage,
					},
				],
			},
			{
				path: "dossier/:dossierDisplayId",
				name: "SingleDossier",
				component: SingleDossierPage,
				redirect: () => {
					const userStore = useUserStore();
					if (userStore.role === "customer") {
						return { name: "Home" };
					}

					return { name: "Financiering" };
				},
				children: [
					{
						path: "financiering",
						name: "Financiering",
						redirect: { name: "Financieringsopzet" },
						component: FinancieringPage,
						children: [
							{
								path: "financieringsopzet",
								name: "Financieringsopzet",
								component: FinancieringsOpzetPage,
							},
							{
								path: "woning",
								name: "Woning",
								component: WoningPage,
							},
							{
								// Compatibiliteit voor opgeslagen links naar de verwijderde
								// Klantgegevens-tab. De verplichtingenvelden staan nu hier;
								// het bestaande dossier-ID blijft onderdeel van de route.
								path: "klantgegevens",
								name: "Klantgegevens",
								redirect: (to) => ({
									name: "Verplichtingen",
									params: to.params,
								}),
							},
							{
								path: "hypotheek",
								name: "Hypotheek",
								component: LeningdelenPage,
							},
							{
								path: "maximale-hypotheek",
								name: "Maximale Hypotheek",
								component: MaximaleHypotheekPage,
							},
							{
								path: "pdf-maken",
								name: "PDF maken",
								component: PDFMakenPage,
							},
							{
								path: "maandlasten",
								name: "Maandlasten",
								component: MaandlastenPage,
							},
							{
								path: "optimalisatie",
								meta: { requiresIntermediary: true },
								name: "Optimalisatie",
								component: OptimalisatiePage,
							},
						],
					},
					{
						path: "advies",
						name: "Advies",
						meta: {
							requiresIntermediary: true,
						},
						redirect: { name: "Pensioenadvies" },
						component: AdvicePage,
						children: [
							{
								path: "pensioen",
								name: "Pensioenadvies",
								component: PensioenAdvicePage,
							},
						],
					},
					{
						path: "documenten",
						name: "Documenten",
						meta: { requiresIntermediary: true },
						component: FileUploadPage,
					},
					{
						path: "inventarisatie",
						name: "Inventarisation",
						meta: { requiresIntermediary: true },
						redirect: { name: "Inkomen" },
						component: InventarisationPage,
						children: [
							{
								path: "gegevens-ophalen",
								name: "Gegevens ophalen",
								component: DataRequestsPage,
							},
							{
								path: "klanten",
								name: "NAW-gegevens",
								component: InventarisationCustomerPage,
							},
							{
								path: "inkomen",
								name: "Inkomen",
								component: InventarisationIncomePage,
							},
							{
								path: "verplichtingen",
								name: "Verplichtingen",
								component: InventarisationLiabilitiesPage,
							},
							{
								path: "vermogen",
								name: "Vermogen",
								component: InventarisationAssetsPage,
							},
							{
								path: "woonsituatie",
								name: "Woonsituatie",
								component: InventarisationCurrentObjectsPage,
							},
							{
								path: "huidige-hypotheek",
								name: "HuidigeHypotheek",
								component: ExistingMortgagePage,
							},
						],
					},
					{
						path: "aanvragen",
						meta: { requiresIntermediary: true },
						children: [
							{
								path: "",
								name: "LoanApplicationOverviewPage",
								component: LoanApplicationOverviewPage,
							},
							{
								path: ":loanApplicationId",
								children: [
									{
										path: "",
										name: "LoanApplicationDetailsPage",
										component: LoanApplicationDetailsPage,
									},
									{
										path: "offers",
										name: "LoanOffersPage",
										component: LoanOffersPage,
									},
									{
										path: "bewerken",
										name: "LoanApplicationEditPage",
										component: LoanApplicationEditPage,
									},
									{
										path: "documenten/:documentId?",
										name: "LoanApplicationDocumentsPage",
										component: LoanApplicationDocumentsPage,
									},
									{
										path: "berichten",
										name: "LoanApplicationMessagesPage",
										component: LoanApplicationMessagesPage,
									},
								],
							},
						],
					},
					{
						path: "instellingen",
						meta: { requiresIntermediary: true },
						name: "Instellingen",
						component: SettingsPage,
					},
					{
						path: "",
						name: "Home",
						component: DossierHomePage,
					},
				],
			},
			{
				path: "/:catchAll(.*)*",
				component: NotFoundPage,
				name: "NotFound",
			},
		],
	},
];

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes,
});

router.beforeEach(async (to, from) => {
	const auth = getAuth();
	const user = await useUserStore().setUser(auth.currentUser);

	const routeIsPublic = to.matched.some((record) => record.meta.public);
	if (!routeIsPublic && !user) {
		return { name: "Login", query: { next: to.fullPath } };
	}

	const routeRequiresLoggedOut = to.matched.some(
		(record) => record.meta.requiresLoggedOut,
	);
	if (routeRequiresLoggedOut && user) {
		return { name: "App" };
	}

	const routeRequiresIntermediary = to.matched.some(
		(record) => record.meta.requiresIntermediary,
	);
	if (
		routeRequiresIntermediary &&
		!(user?.role && ["intermediary", "admin"].includes(user.role))
	) {
		if (user?.role === "transcription") {
			return { name: "MeetingsOverview" };
		}

		// Redirect to the dossier of the customer
		const zero = useZero().value;
		const availableDossiers = await zero.run(queries.dossier.all());
		const sortedList = availableDossiers?.toSorted(
			(a, b) => (b.creationDate ?? 0) - (a.creationDate ?? 0),
		);
		const dossierToSelect = sortedList?.[0]?.displayId;

		return {
			name: "SingleDossier",
			params: { dossierDisplayId: dossierToSelect },
		};
	}

	document.title = "LoanLink";

	if (typeof to.meta?.title === "string" && to.meta?.title) {
		document.title = `${to.meta.title} - Loanlink`;
	}

	if (typeof to.meta?.title === "function") {
		const title = to.meta.title(to, from);

		if (title) {
			document.title = `${title} - Loanlink`;
		}
	}
});

export default router;
